import {Component} from '@angular/core';
import {ActionsSubject, Store} from '@ngrx/store';
import {AbstractWidgetDirective} from '../../abstract-widget.directive';
import {EVAL_REQUIRED_VALIDATOR_KEY, UiWidget, WidgetConfiguration, WidgetValidator} from '@pt/pt-models';
import {TranslateService} from '@ngx-translate/core';
import {WidgetMultiQuestionPreviewV0Dto} from './models/widget-multi-question-preview-v0.dto';
import {WidgetMultiQuestionValueV0 } from './models/widget-multi-question-value-v0.dto';
import {QueryService} from '@pt/api-client';
import {EvalQuestionResponse, WidgetMultiQuestionResponseV0Dto} from './models/widget-multi-question-response-v0.dto';
import {WidgetMultiQuestionAgrsDataV0Dto} from './models/widget-multi-question-agrs-data-v0.dto';
import {WidgetMultiQuestionHeaderV0Dto} from './models/widget-multi-question-header-v0.dto';
import {WidgetUtils} from '../../widget.utils';
import {NgxPermissionsService} from 'ngx-permissions';
import { WidgetDialogService } from '../../../services/widget-dialog.service';

@Component({
  selector: 'pt-widget-multi-question-v0',
  template: `

    <div class='position-relative' *ngIf="internalStatus.enabled">
      <pt-widget-disabled
        *ngIf="internalStatus.viewMode === 'LOADING'"
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'
        [label]='widget?.label'
        [help]='widget?.help'

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'

        (clickSaveWidget)='saveWidget(false)'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <ng-container *ngIf='!internalStatus.showOptButton'>

        <pt-widget-multi-question-v0-editor
          [show]="internalStatus.viewMode === 'EDIT' || internalStatus.viewMode === 'LOADING'"
          [data]='editorData'
          [properties]="properties"
          [noResponseLabel]="'LABELS.NO_RESPONSE' | translate"
          (changeValue)='changeCurrentValue($event)'>
        </pt-widget-multi-question-v0-editor>

        <pt-widget-multi-question-v0-preview
          *ngIf="internalStatus.viewMode === 'PREVIEW'"
          [noDataLabel]="noDataLabel"
          [properties]="properties"
          [loading]='internalStatus.isLoading'
          [showNoData]='widget?.editable'
          [noResponseLabel]="'LABELS.NO_RESPONSE' | translate"
          [previewData]='previewData'>
        </pt-widget-multi-question-v0-preview>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]='internalStatus.errorMessage | async'>
        </pt-widget-error>

        <pt-widget-footer></pt-widget-footer>
      </ng-container>
    </div>
  `
})
export class WidgetMultiQuestionV0Component extends AbstractWidgetDirective<WidgetMultiQuestionPreviewV0Dto> {
  editorData: WidgetMultiQuestionPreviewV0Dto;
  private currentValue: WidgetMultiQuestionValueV0;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();

    this.service.resolve(widget?.args?.query).subscribe(
      (queryResponse: unknown[]) => {
        const header: WidgetMultiQuestionHeaderV0Dto = widget?.args?.header;
        const mapping = widget?.args?.query?.mapping;
        this.currentValue = widget.value ? widget.value : null;
        const questions: WidgetMultiQuestionResponseV0Dto[] = WidgetUtils.mappingQueryResponse(queryResponse, mapping) as WidgetMultiQuestionResponseV0Dto[];

        if (this.currentValue?.answers?.length > 0) {
          questions?.forEach(q => {
            this.currentValue?.answers?.forEach((a) => {
              if (a.uuid === q.uuid) {
                switch (q.type) {
                  case 'CHECKBOX':
                    q.checkboxValue = a.value as number[];
                    break;
                  case 'RADIO_BUTTON':
                    q.radioValue = a.value as number;
                    break;
                  case 'TEXT':
                    q.textValue = a.value as string;
                    break;
                }
                q.note = a.note;
              }
            });
          });
        }
        const data: WidgetMultiQuestionAgrsDataV0Dto = widget?.args?.data;
        const valutation: EvalQuestionResponse = {
          name: data.evalQuestion?.name,
          values: data.evalQuestion?.values,
          checkedValueIndex: this.currentValue?.evalAnswer
        } as EvalQuestionResponse;

        this.editorData = {
          label: widget?.label,
          help: widget?.help,
          header,
          questions,
          valutation
        } as WidgetMultiQuestionPreviewV0Dto;

        this.valueController(widget, questions);

        this.checkValidity();

        this.dispatchCompleteState(this.editorData);
      }, () => {
        this.dispatchErrorState();
      }
    );
  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = this.currentValue;
    widget.comment = this.comment;
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  // Override
  isNoData(): boolean {
    const value = this.widget.value as WidgetMultiQuestionValueV0;
    return super.isNoData()
      || (
        (value?.evalAnswer === null || value?.evalAnswer === undefined)
        && (!value?.answers || value?.answers?.length === 0)
      );
  }

  changeCurrentValue($event: WidgetMultiQuestionValueV0 | undefined) {
    if (JSON.stringify(this.currentValue) !== JSON.stringify($event)) {
      this.currentValue = $event;
      this.checkValidity();

      if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.internalStatus.isDraft) {
        this.saveWidget();
        this.loadWidget({...this.widget, value: this.currentValue});
      }
    }
  }

  configuration(): WidgetConfiguration {
    return {supportAutoSave: true, supportValidation: false};
  }


  private checkValidity() {
    const evalRequired = this.widget?.validators?.find((w: WidgetValidator) => w.key === EVAL_REQUIRED_VALIDATOR_KEY && w.args?.length === 1 && w.args[0])
    const questionRequired = this.widget?.validators?.find((w: WidgetValidator) => w.key === EVAL_REQUIRED_VALIDATOR_KEY && w.args?.length === 1 && w.args[0])

    let isValid = true;

    if (this.widget?.opt) {
      isValid = true;
    } else {
      if (evalRequired && (this.currentValue.evalAnswer === null || this.currentValue.evalAnswer === undefined)) {
        isValid = false;
      }

      if (questionRequired && (this.currentValue?.answers?.filter((a) => a.value === null || a.value === undefined)?.length !== 0)) {
        isValid = false;
      }
    }

    this.isValid$.next(isValid);
  }


  private valueController(widget: UiWidget, data: WidgetMultiQuestionResponseV0Dto[]) {

    const noData = !data || data?.length === 0;
    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }
  }
}
